/**
 * Single entry point for sending a lead to the server.
 *
 * Forms no longer talk to the CRM directly: they post here, this posts to
 * the Netlify function at /api/lead, and the function is the only thing
 * holding the CRM credentials.
 */

const LEAD_ENDPOINT = "/api/lead"

export const GENERIC_ERROR =
  "We couldn't submit your request. Please try again."

export const VALIDATION_ERROR =
  "Please check the highlighted fields and try again."

/**
 * Reads the UTM values this site persists in sessionStorage.
 *
 * Mirrors the behaviour the forms had inline: lead_source falls back to
 * "website" when no utm_source was ever captured.
 */
export const getStoredAttribution = () => {
  const attribution = {
    utm_medium: "",
    utm_term: "",
    utm_campaign: "",
    utm_content: "",
    lead_source: "website",
  }

  if (typeof window === "undefined") return attribution

  try {
    const source = sessionStorage.getItem("utm_source")
    if (source !== "" && source !== null) {
      attribution.lead_source = source
      attribution.utm_medium = sessionStorage.getItem("utm_medium") || ""
      attribution.utm_term = sessionStorage.getItem("utm_term") || ""
      attribution.utm_campaign = sessionStorage.getItem("utm_campaign") || ""
      attribution.utm_content = sessionStorage.getItem("utm_content") || ""
    }
  } catch (e) {
    // Private browsing can throw on sessionStorage access; attribution is
    // optional, so fall through with the defaults rather than blocking a lead.
  }

  return attribution
}

/**
 * Stores UTM parameters from the current URL, as the forms did on mount.
 */
export const captureAttribution = () => {
  if (typeof window === "undefined") return

  try {
    const searchParams = new URLSearchParams(window.location.search)
    const source = searchParams.get("utm_source")
    if (source !== "" && source !== null) {
      sessionStorage.setItem("utm_source", source)
      sessionStorage.setItem("utm_medium", searchParams.get("utm_medium"))
      sessionStorage.setItem("utm_campaign", searchParams.get("utm_campaign"))
      sessionStorage.setItem("utm_term", searchParams.get("utm_term"))
      sessionStorage.setItem("utm_content", searchParams.get("utm_content"))
    }
  } catch (e) {
    // Non-fatal: attribution is best-effort.
  }
}

/**
 * Pushes the existing GTM lead event.
 *
 * Event name and shape are unchanged from the original inline code because
 * live Google Ads conversions are keyed to them. Only the timing changed:
 * this now runs after the server confirms the CRM accepted the lead.
 */
export const pushLeadConversion = ({ email, phone }) => {
  if (typeof window === "undefined") return
  if (typeof window.dataLayer === "undefined") return

  window.dataLayer.push({
    event: "form_submit",
    formName: "website_forms",
    enhanced_conversion_data: {
      email: email,
      phone_number: phone,
    },
  })
}

/**
 * Posts a lead payload to the Netlify function.
 *
 * Resolves only when the server confirms the lead was accepted and the CRM
 * took it. Any other outcome throws, so callers never fire a conversion for
 * a lead that did not land.
 */
export const submitLead = async payload => {
  let response
  let result

  try {
    response = await fetch(LEAD_ENDPOINT, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    })
    result = await response.json()
  } catch (e) {
    // Network failure or a non-JSON response; details stay out of the UI.
    throw new Error(GENERIC_ERROR)
  }

  if (!response.ok || !result || !result.accepted) {
    // A spam-blocked request returns { ok: true, accepted: false }, which
    // lands here too: the visitor sees a normal error and the bot learns
    // nothing about why it was rejected.
    const error = new Error(
      result && result.errors ? VALIDATION_ERROR : GENERIC_ERROR
    )
    error.fieldErrors = (result && result.errors) || {}
    throw error
  }

  return result
}
